/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useState, useRef } from "react";

import { useSelector } from "react-redux";
import styled from "styled-components";

import { Block } from "src/classes/block";
import { Plate } from "src/classes/plate/Plate";
import { PositionUtils } from "src/classes/utilities/PositionUtils";
import { BlockContent } from "src/components/Block/BlockContent";
import { PlateBlockTopbar } from "src/components/Block/BlockTopbar/PlateBlockTopbar";
import { BlockContentContainer } from "src/components/Block/styles";
import { ContextMenu } from "src/components/UI/ContextMenu";
import { AppState } from "src/store";

interface Props {
  plate: Plate;
  block: Block;
}

export const PlateBlock = ({ block, plate }: Props) => {
  const blockRef = useRef<HTMLDivElement>(null);

  const editable = plate.observe((state) => state.editable);

  const selection = plate.observe((state) => state.selection);
  const isSelected = selection.includes(block.snapshot.id);

  const lockedBlocks = plate.socket.observe((state) => state.lockedBlocks);
  const isLocked = lockedBlocks.map(({ blockId: id }) => id).includes(block.snapshot.id);

  const isScreenshotter = useSelector((state: AppState) => state.globalUI.uiSettings.isScreenshotter);

  const [hovering, setHovering] = useState(false);

  const zIndex = block.observe((state) => state.zIndex);
  const top = block.observe((state) => state.top);
  const left = block.observe((state) => state.left);
  const width = block.observe((state) => state.width);
  const height = block.observe((state) => state.height);

  return (
    <ContextMenu block={block} editable={editable} plate={null}>
      <BlockContentContainer
        ref={blockRef}
        isScreenshotter={isScreenshotter}
        isSelected={isSelected}
        editable={editable && !isLocked}
        boxShadow={false}
        onMouseDown={(e) => editable && plate.onMouseDownBlock(block, e)}
        onMouseUp={() => plate.onMouseUpBlock(block)}
        onMouseEnter={() => {
          setHovering(true);
        }}
        onMouseLeave={() => setHovering(false)}
        style={{
          top: PositionUtils.snapToGrid(top),
          left: PositionUtils.snapToGrid(left),
          width: PositionUtils.snapToGrid(width) + 1,
          height: PositionUtils.snapToGrid(height) + 1,
          zIndex,
        }}
      >
        <PlateBlockTopbar plate={plate} block={block} hovering={hovering} />
        <BlockContent block={block} editable={editable && !isLocked} plate={plate} />
        {editable && !isLocked && (
          <ResizeHitbox onMouseDown={(e) => plate.onResizeStart(block, e)}>
            <ResizeGrabber />
          </ResizeHitbox>
        )}
      </BlockContentContainer>
    </ContextMenu>
  );
};

const ResizeHitbox = styled.div`
  height: 12px;
  width: 12px;
  position: absolute;
  bottom: 0px;
  right: 0px;
  cursor: se-resize;
  &:active {
    cursor: se-resize;
  }
`;

const ResizeGrabber = styled.div`
  height: 8px;
  width: 9px;
  position: absolute;
  bottom: 0px;
  right: 0px;
  background: ${({ theme }) => theme.background};
  opacity: 80%;
  border-left: ${({ theme }) => `1px solid ${theme.block.border}`};
  border-top: ${({ theme }) => `1px solid ${theme.block.border}`};
`;
